import React from 'react';
import { X, Send, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const DispatchConfirmationModal = ({ 
  workPeriod, 
  dispatchMessage, 
  setDispatchMessage, 
  onConfirm, 
  onCancel,
  projects = [] // Add projects prop to access project information
}) => {
  // Get crew members from work period
  const crewMembers = workPeriod.resources?.filter(r => r.type === 'crew') || [];
  
  // Get project details to show project number instead of UUID
  const project = projects.find(p => p.id === workPeriod.project_id);
  const projectDisplay = project?.project_number || project?.title || workPeriod.project_id;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl mx-4 bg-white border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-700" />
            Dispatch Crew — Job Chat
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="text-slate-500 hover:text-slate-900"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Work Period Info */}
          <div className="bg-slate-100 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Work Period Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Project:</span>
                <span className="text-slate-900 ml-2">{projectDisplay}</span>
              </div>
              <div>
                <span className="text-slate-500">Date:</span>
                <span className="text-slate-900 ml-2">
                  {workPeriod.start_date === workPeriod.end_date 
                    ? workPeriod.start_date 
                    : `${workPeriod.start_date} to ${workPeriod.end_date}`
                  }
                </span>
              </div>
              <div>
                <span className="text-slate-500">Time:</span>
                <span className="text-slate-900 ml-2">{workPeriod.start_time} - {workPeriod.end_time}</span>
              </div>
              <div>
                <span className="text-slate-500">Crew Members:</span>
                <span className="text-slate-900 ml-2">{crewMembers.length}</span>
              </div>
            </div>
          </div>

          {/* Crew Members List */}
          <div className="bg-slate-100 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Dispatching to:</h3>
            <div className="space-y-2">
              {crewMembers.map((crew, index) => {
                const isForeman = workPeriod.foreman_id === crew.id;
                return (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-slate-800">
                      {isForeman && '👑 '}
                      {crew.name}
                      {isForeman && <span className="ml-2 text-xs text-amber-700">(Foreman)</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Message Preview */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-800">Dispatch message (posted to the job chat):</label>
            <textarea
              value={dispatchMessage}
              onChange={(e) => setDispatchMessage(e.target.value)}
              className="w-full h-40 p-3 bg-white border border-slate-300 rounded-lg text-slate-800 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              placeholder="Enter your dispatch message..."
              style={{color: '#f3f4f6'}}
            />
            <div className="text-xs text-slate-700">
              Every crew member gets a push notification and confirms with ✓ / ✗ in the chat. The PM and dispatcher see everything.
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={onCancel}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
              disabled={!dispatchMessage.trim() || crewMembers.length === 0}
            >
              <Send className="h-4 w-4" />
              Post dispatch to {crewMembers.length} crew member{crewMembers.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DispatchConfirmationModal;